import { useState } from 'react'
import Modal from '@components/common/Modal'
import reportFeedbackService from '@/api/v1/reportFeedbackService'

/**
 * Beta feedback control (spec 123).
 *
 * 🔴 Rendered INSIDE `ReportActionBar`, not passed to it as a prop.
 *
 * ReportActionBar is the one piece of chrome both hosts render — `ReportView`
 * for the 119 generic reports and `SpecializedReportShell` for the 21
 * specialized ones — so rendering here reaches all 140 by construction. A
 * `feedback` prop would have to be passed by both call sites, and "a page-opt-in
 * prop GUARANTEES the feature is missing somewhere" is a documented, repeatedly
 * paid-for failure in this repo (the whole CC-50013 family).
 *
 * The report context travels with the feedback because that is most of its
 * value: "this is slow" is worth little, "this is slow on report 190" joins
 * straight onto the run timings already in the same database.
 */

const KINDS = [
  ['bug', 'Something is broken'],
  ['idea', 'Idea or request'],
  ['question', 'Question'],
  ['praise', 'This worked well'],
]
const SEVERITIES = [
  ['blocking', 'Blocking my work'],
  ['major', 'Major annoyance'],
  ['minor', 'Minor'],
]

/** Mirrors cb-api's cap so the UI cannot submit more than the server keeps. */
const MAX_SUMMARY = 200
const MAX_DETAIL = 4000

const ReportFeedback = ({ reportId, reportKind, reportName }) => {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState('bug')
  const [severity, setSeverity] = useState('minor')
  const [summary, setSummary] = useState('')
  const [detail, setDetail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  const close = () => {
    setOpen(false)
    // Reset only AFTER a successful send. A failed submit keeps what the person
    // typed — losing it is how a bug report stops being filed at all.
    if (done) {
      setSummary(''); setDetail(''); setKind('bug'); setSeverity('minor')
      setDone(false); setError(null)
    }
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!summary.trim() || busy) return
    setBusy(true)
    setError(null)
    const res = await reportFeedbackService.submit({
      kind,
      severity: kind === 'bug' ? severity : undefined,
      summary: summary.trim(),
      detail: detail.trim() || undefined,
      reportId: reportId != null ? String(reportId) : undefined,
      reportKind,
      page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      /* eslint-disable-next-line no-undef -- Vite build-time global (vite.config.js
         `define`), which substitutes the bare token only; globalThis.__APP_VERSION__
         would NOT be replaced and would read undefined at runtime. Knowing which
         build a report came from is most of triage. */
      appVersion: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : undefined,
    })
    setBusy(false)
    if (res.ok) setDone(true)
    else setError(res.error)
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-rc-pill"
        aria-haspopup="dialog"
        // A popup trigger says whether its popup is open (spec 146): the same pair
        // CategorySelect and PopoverMenu use, and the grid audit's ADA rule, which
        // this button failed on every report page from #295 until then.
        aria-expanded={open}
      >
        Feedback
      </button>

      {open && (
        <Modal
          title={done ? 'Thank you' : 'Send feedback'}
          subtitle={done ? null : reportName ? `About "${reportName}"` : 'About Report Center'}
          onClose={close}
          maxWidth="max-w-lg"
          footer={
            done ? (
              <button type="button" className="btn-cb-primary" onClick={close}>Close</button>
            ) : (
              <div className="flex items-center justify-end gap-2">
                <button type="button" className="btn-rc" onClick={close} disabled={busy}>Cancel</button>
                <button
                  type="submit"
                  form="rc-feedback-form"
                  className="btn-cb-primary"
                  disabled={busy || !summary.trim()}
                >
                  {busy ? 'Sending…' : 'Send'}
                </button>
              </div>
            )
          }
        >
          {done ? (
            <p className="text-sm text-gray-600">
              Filed. It goes to the Report Center team with the report you were on — you don&apos;t
              need to describe where you were.
            </p>
          ) : (
            <form id="rc-feedback-form" onSubmit={submit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="rc-fb-kind" className="block text-sm font-medium text-cb-navy mb-1">
                  What kind of feedback?
                </label>
                <select
                  id="rc-fb-kind"
                  className="select-rc w-full"
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                >
                  {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              {kind === 'bug' && (
                <div>
                  <label htmlFor="rc-fb-sev" className="block text-sm font-medium text-cb-navy mb-1">
                    How much is it holding you up?
                  </label>
                  <select
                    id="rc-fb-sev"
                    className="select-rc w-full"
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                  >
                    {SEVERITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="rc-fb-summary" className="block text-sm font-medium text-cb-navy mb-1">
                  In one line
                </label>
                <input
                  id="rc-fb-summary"
                  className="input-rc w-full"
                  value={summary}
                  maxLength={MAX_SUMMARY}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Totals do not match the export"
                  required
                />
              </div>

              <div>
                <label htmlFor="rc-fb-detail" className="block text-sm font-medium text-cb-navy mb-1">
                  Anything else <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <textarea
                  id="rc-fb-detail"
                  className="input-rc w-full"
                  rows={5}
                  value={detail}
                  maxLength={MAX_DETAIL}
                  onChange={(e) => setDetail(e.target.value)}
                  placeholder="What you expected, and what happened instead."
                />
              </div>

              {error && (
                <p role="alert" className="text-sm text-red-600">{error}</p>
              )}
            </form>
          )}
        </Modal>
      )}
    </>
  )
}

export default ReportFeedback
